import { cloneDocumentForCommand } from "@/document/model/documentClone";
import type { Command, CommandContext, CommandResult, EditorSelectionState } from "./Command";

export interface HistoryState {
  undoDepth: number;
  redoDepth: number;
  dirtyRevision: number;
}

export class CommandHistory {
  private readonly undoStack: HistoryEntry[] = [];
  private readonly redoStack: HistoryEntry[] = [];
  private savedRevision = 0;

  execute(command: Command, context: CommandContext, options: { afterSelection?: EditorSelectionState } = {}): CommandResult {
    const previousSelection = cloneSelection(context.selection);

    try {
      const result = command.execute(context);
      if (command.affectsDocument !== false) {
        context.document.revision += 1;
        context.document.metadata.updatedAt = new Date().toISOString();
      }

      const entry: HistoryEntry = {
        command,
        beforeSelection: previousSelection,
        afterSelection: cloneSelection(options.afterSelection ?? context.selection),
      };
      context.selection = cloneSelection(entry.afterSelection);
      const previous = this.undoStack.at(-1);
      if (previous?.command.canMergeWith?.(command) && previous.command.merge) {
        this.undoStack[this.undoStack.length - 1] = {
          command: previous.command.merge(command),
          beforeSelection: previous.beforeSelection,
          afterSelection: entry.afterSelection,
        };
      } else {
        this.undoStack.push(entry);
      }

      this.redoStack.length = 0;
      return result;
    } catch (error) {
      // Working copy may be partially mutated; callers discard the context.
      context.selection = previousSelection;
      throw error;
    }
  }

  undo(context: CommandContext): CommandResult | null {
    const entry = this.undoStack.pop();
    if (!entry) {
      return null;
    }

    const rollbackDocument = entry.command.affectsDocument === false
      ? undefined
      : cloneDocumentForCommand(context.document);
    const previousSelection = cloneSelection(context.selection);
    try {
      const result = entry.command.undo(context);
      context.selection = cloneSelection(entry.beforeSelection);
      if (entry.command.affectsDocument !== false) {
        context.document.revision += 1;
        context.document.metadata.updatedAt = new Date().toISOString();
      }
      this.redoStack.push(entry);
      return result;
    } catch (error) {
      if (rollbackDocument) {
        context.document = rollbackDocument;
      }
      context.selection = previousSelection;
      this.undoStack.push(entry);
      throw error;
    }
  }

  redo(context: CommandContext): CommandResult | null {
    const entry = this.redoStack.pop();
    if (!entry) {
      return null;
    }

    const rollbackDocument = entry.command.affectsDocument === false
      ? undefined
      : cloneDocumentForCommand(context.document);
    const previousSelection = cloneSelection(context.selection);
    try {
      const result = entry.command.redo(context);
      context.selection = cloneSelection(entry.afterSelection);
      if (entry.command.affectsDocument !== false) {
        context.document.revision += 1;
        context.document.metadata.updatedAt = new Date().toISOString();
      }
      this.undoStack.push(entry);
      return result;
    } catch (error) {
      if (rollbackDocument) {
        context.document = rollbackDocument;
      }
      context.selection = previousSelection;
      this.redoStack.push(entry);
      throw error;
    }
  }

  markSaved(documentRevision: number): void {
    this.savedRevision = documentRevision;
  }

  getState(currentRevision: number): HistoryState {
    return {
      undoDepth: this.undoStack.length,
      redoDepth: this.redoStack.length,
      dirtyRevision: currentRevision - this.savedRevision,
    };
  }
}

interface HistoryEntry {
  command: Command;
  beforeSelection?: EditorSelectionState;
  afterSelection?: EditorSelectionState;
}

function cloneSelection(selection: EditorSelectionState | undefined): EditorSelectionState | undefined {
  return selection ? structuredClone(selection) : undefined;
}
